import React, { useState, useEffect, useRef, useCallback } from 'react';
import Editor from '@monaco-editor/react';
import { useTheme } from 'next-themes';
import { useUserVFS, type UserFile } from '@os/hooks/useUserVFS';
import { useToast } from '@os/hooks/use-toast';
import { useSettings } from '@os/hooks/useSettings';
import { Button } from '@os/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@os/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@os/components/ui/tabs';
import { ScrollArea } from '@os/components/ui/scroll-area';
import { Input } from '@os/components/ui/input';
import { Label } from '@os/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@os/components/ui/dialog';
import {
  Menubar,
  MenubarContent,
  MenubarItem,
  MenubarMenu,
  MenubarSeparator,
  MenubarTrigger,
} from '@os/components/ui/menubar';
import { 
  Save, 
  FolderOpen, 
  FileText, 
  Settings, 
  Search, 
  Replace,
  Play,
  Code,
  File,
  Folder,
  Plus,
  X,
  Minus,
  Square,
  ChevronLeft,
  ChevronRight,
  FileIcon,
  FolderPlus,
  Download,
  Upload,
  RotateCcw,
  Copy,
  Scissors,
  ClipboardPaste,
  RotateCw,
  Undo2,
  Info,
  HelpCircle,
  ExternalLink,
  Heart,
  Monitor,
  Cpu,
  MemoryStick,
  Gamepad2,
  Sparkles,
  Hammer
} from 'lucide-react';
import { saveAs } from 'file-saver';
import { SaveAsDialog } from './SaveAsDialog';
import { ProjectManager } from './ProjectManager';
import FileTreeExplorer from './FileTreeExplorer';
import { type EditorSettingsConfig, defaultSettings } from './EditorSettings';
import ReactRunner from './ReactRunner';
import CppRunner from './CppRunner';
import AIBuildPanel from './AIBuildPanel';
import { ensureDir, ensureFile } from '../../utils/filesystem';
import EditorTabBar from './EditorTabBar';
import { cn } from '@os/lib/utils';

interface FileTab {
  id: string;
  name: string;
  path: string;
  content: string;
  isDirty: boolean;
  language: string;
}

interface Project {
  id: string;
  name: string;
  path: string;
  type: string;
  description: string;
  created: string;
  lastModified: string;
}

interface CodeEditorProps {
  onOpenWindow?: (appId: string, title: string, filePath?: string, content?: string) => void;
}

const CodeEditor: React.FC<CodeEditorProps> = ({ onOpenWindow }) => {
  const { theme } = useTheme();
  const { createFile, getFile, listFiles, updateFile, reloadFile, fileExists } = useUserVFS();
  const { toast } = useToast();
  const [isBuilding, setIsBuilding] = useState(false);
  
  const [activeTabId, setActiveTabId] = useState<string>('');
  const [tabs, setTabs] = useState<FileTab[]>([]);
  const [showFileExplorer, setShowFileExplorer] = useState(true);
  const [showProjectManager, setShowProjectManager] = useState(false);
  const [currentProject, setCurrentProject] = useState<Project | null>(null);
  const [currentPath, setCurrentPath] = useState('');
  const [fileItems, setFileItems] = useState<UserFile[]>([]);
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [showAboutDialog, setShowAboutDialog] = useState(false);
  const [showOpenProjectDialog, setShowOpenProjectDialog] = useState(false);
  const [availableProjects, setAvailableProjects] = useState<Project[]>([]);
  const { settings, saveSettings } = useSettings('editor-settings', defaultSettings);
  const [editorSettings, setEditorSettings] = useState<EditorSettingsConfig>(settings as EditorSettingsConfig);
  const [cursorPosition, setCursorPosition] = useState({ line: 1, column: 1 });
  const [selection, setSelection] = useState({ selectedChars: 0, selectedLines: 0 });
  const [showRunner, setShowRunner] = useState(false);
  const [showCppRunner, setShowCppRunner] = useState(false);
  const [showAIBuildPanel, setShowAIBuildPanel] = useState(false);

  const editorRef = useRef<any>(null);

  // Update local settings when Supabase settings change
  useEffect(() => {
    setEditorSettings(settings as EditorSettingsConfig);
  }, [settings]);

  // Listen for settings changes from the settings window
  useEffect(() => {
    const handleSettingsChange = (event: CustomEvent) => {
      const newSettings = event.detail as EditorSettingsConfig;
      setEditorSettings(newSettings);
      saveSettings(newSettings);
    };

    window.addEventListener('editorSettingsChanged', handleSettingsChange as EventListener);

    return () => {
      window.removeEventListener('editorSettingsChanged', handleSettingsChange as EventListener);
    };
  }, [saveSettings]);

  // NEW: Build (compile WAT → WASM) for the current project or active .wat file
  const buildProject = useCallback(async () => {
    if (!currentProject) {
      toast({
        title: "No project",
        description: "Open a project with a WAT source to build.",
        variant: "destructive",
      });
      return;
    }
    setIsBuilding(true);
    try {
      const wabtFactory = (await import('wabt')).default as any;
      const wabtInstance = await wabtFactory();

      // Helper to try both slash styles against VFS and log attempts
      const pathVariants = (p: string) => {
        const set = new Set<string>();
        set.add(p);
        set.add(p.replace(/\\/g, '/'));
        set.add(p.replace(/\//g, '\\'));
        return Array.from(set);
      };
      const findExistingFile = (paths: string[]) => {
        for (const base of paths) {
          for (const pv of pathVariants(base)) {
            const f = getFile(pv);
            if (f?.content && f.content.trim().length > 0) {
              console.log('[Build] Found file at:', pv);
              return { path: pv, content: f.content.trim() };
            } else {
              console.log('[Build] Not found at:', pv);
            }
          }
        }
        return null;
      };

      // Active tab context
      const currActiveTab = tabs.find(t => t.id === activeTabId);
      const activePath = currActiveTab?.path || '';

      // Try manifest to guide entry (AssemblyScript template: entry.wasm = "build/module.wasm")
      const manifestCandidates = [
        `${currentProject.path}\\manifest.json`,
        `${currentProject.path}\\src\\manifest.json`,
      ];
      const foundManifest = findExistingFile(manifestCandidates);
      let manifestEntryWasm: string | null = null;
      if (foundManifest) {
        try {
          const manifest = JSON.parse(foundManifest.content);
          if (manifest?.entry?.wasm && typeof manifest.entry.wasm === 'string') {
            manifestEntryWasm = `${currentProject.path}\\${manifest.entry.wasm}`.replace(/\//g, '\\');
          }
        } catch (e) {
          console.log('[Build] Manifest parse error:', e);
        }
      }

      // Primary WAT candidates (prefer actual .wat files)
      const watCandidates: string[] = [];
      if (activePath.toLowerCase().endsWith('.wat')) {
        watCandidates.push(activePath);
      }
      watCandidates.push(
        `${currentProject.path}\\build\\module.wat`,
        `${currentProject.path}\\src\\build\\module.wat`,
        `${currentProject.path}\\src\\module.wat`
      );
      console.log('[Build] WAT candidates:', watCandidates);

      const foundWat = findExistingFile(watCandidates);
      const watPath = foundWat?.path ?? null;
      const watContent = foundWat?.content ?? null;

      if (watPath && watContent) {
        // Compile WAT → WASM
        const parsed = wabtInstance.parseWat('module.wat', watContent);
        const binary = parsed.toBinary({ log: false });
        const bytes = new Uint8Array(binary.buffer);

        let targetWasmPath = watPath.replace(/\.wat$/i, '.wasm').replace(/\//g, '\\');
        const outDir = targetWasmPath.slice(0, Math.max(targetWasmPath.lastIndexOf('\\'), targetWasmPath.lastIndexOf('/')));
        try { await ensureDir(outDir); } catch {}

        // Base64 encode for storage
        let b64 = '';
        for (let i = 0; i < bytes.length; i++) b64 += String.fromCharCode(bytes[i]);
        const base64Wasm = btoa(b64);

        await createFile(targetWasmPath, base64Wasm, false, 'application/wasm');
        await ensureFile(targetWasmPath, base64Wasm);

        toast({
          title: "Build succeeded",
          description: `Compiled and saved ${targetWasmPath.replace(currentProject.path + '\\\\','').replace(currentProject.path + '/', '')}`,
        });
        return;
      }

      // No .wat found — consider .wasm entries, including manifest entry
      const wasmCandidates = [
        ...(manifestEntryWasm ? [manifestEntryWasm] : []),
        `${currentProject.path}\\build\\module.wasm`,
        `${currentProject.path}\\src\\build\\module.wasm`,
        `${currentProject.path}\\src\\module.wasm`,
        // Also consider active file if it ends with .wasm
        ...(activePath.toLowerCase().endsWith('.wasm') ? [activePath] : [])
      ];
      console.log('[Build] WASM candidates:', wasmCandidates);

      const foundWasm = findExistingFile(wasmCandidates);
      if (foundWasm?.path && foundWasm.content) {
        const wasmPathRaw = foundWasm.path;
        const raw = foundWasm.content;
        const wasmPath = wasmPathRaw.replace(/\//g, '\\');

        const outDir = wasmPath.slice(0, Math.max(wasmPath.lastIndexOf('\\'), wasmPath.lastIndexOf('/')));
        try { await ensureDir(outDir); } catch {}

        // Case A: WAT text stored under .wasm (AssemblyScript template)
        if (raw.startsWith('(module')) {
          const parsed = wabtInstance.parseWat('module.wat', raw);
          const binary = parsed.toBinary({ log: false });
          const bytes = new Uint8Array(binary.buffer);

          // Save compiled WASM (Base64) over/next to the .wasm path
          let b64 = '';
          for (let i = 0; i < bytes.length; i++) b64 += String.fromCharCode(bytes[i]);
          const base64Wasm = btoa(b64);

          await createFile(wasmPath, base64Wasm, false, 'application/wasm');
          await ensureFile(wasmPath, base64Wasm);

          // Also emit a sibling .wat for clarity
          const watFromAsmPath = wasmPath.replace(/\.wasm$/i, '.wat');
          await createFile(watFromAsmPath, raw, false, 'application/wat');
          await ensureFile(watFromAsmPath, raw);

          toast({
            title: "Build succeeded",
            description: `Compiled WAT text → ${wasmPath.replace(currentProject.path + '\\\\','').replace(currentProject.path + '/', '')}`,
          });
          return;
        }

        // Case B: Base64 WASM – optionally generate .wat for inspection
        try {
          const bin = atob(raw);
          const bytes = new Uint8Array(bin.length);
          for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);

          // Generate readable WAT next to the WASM
          const mod = wabtInstance.readWasm(bytes, { readDebugNames: true });
          try { mod.generateNames(); mod.applyNames(); } catch {}
          const watText = mod.toText({ foldExprs: false, inlineExport: false });
          mod.dispose();

          const watPathOut = wasmPath.replace(/\.wasm$/i, '.wat');
          await ensureFile(watPathOut, watText);
          await createFile(watPathOut, watText, false, 'application/wat');

          toast({
            title: "Build completed",
            description: `Found existing WASM and generated ${watPathOut.replace(currentProject.path + '\\\\','').replace(currentProject.path + '/', '')}`,
          });
          return;
        } catch (decodeErr) {
          toast({
            title: "Invalid WASM content",
            description: "Expected Base64-encoded WASM bytes or WAT text.",
            variant: "destructive",
          });
          return;
        }
      }

      toast({
        title: "No WAT/WASM source found",
        description: "Checked .wat files, manifest entry.wasm, and typical build locations.",
        variant: "destructive",
      });
      return;
    } catch (err: any) {
      toast({
        title: "Build failed",
        description: err?.message || "Could not compile WAT to WASM.",
        variant: "destructive",
      });
    } finally {
      setIsBuilding(false);
    }
  }, [currentProject, activeTabId, tabs, getFile, createFile, toast]);

  const openCurrentProjectRuntime = useCallback(async () => {
    if (!onOpenWindow || !currentProject) {
      return;
    }

    const manifestPaths = [
      `${currentProject.path}\\manifest.json`,
      `${currentProject.path}\\src\\manifest.json`,
    ];

    let runtimeAppId = 'module-host';
    let runtimeTitle = 'Module Host';

    for (const manifestPath of manifestPaths) {
      const manifestFile = (await reloadFile(manifestPath)) ?? getFile(manifestPath);
      if (!manifestFile?.content) {
        continue;
      }

      try {
        const manifest = JSON.parse(manifestFile.content) as { name?: string; type?: string };
        if (manifest.type === 'web') {
          runtimeAppId = 'web-app-runner';
          runtimeTitle = (typeof manifest.name === 'string' && manifest.name.trim()) || currentProject.name || 'Web App';
        }
        break;
      } catch {
        break;
      }
    }

    onOpenWindow(runtimeAppId, runtimeTitle, currentProject.path);
  }, [currentProject, getFile, onOpenWindow, reloadFile]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // F5 - Run React project
      if (event.key === 'F5') {
        event.preventDefault();
        setShowRunner(true);
      }
      // Ctrl/Cmd+W - Close active tab
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'w') {
        event.preventDefault();
        if (activeTabId) closeTab(activeTabId);
      }
      // Ctrl/Cmd+K then S - Save All (approximation)
      if ((event.ctrlKey || event.metaKey) && event.shiftKey && event.key.toLowerCase() === 's') {
        event.preventDefault();
        saveAllTabs();
      }
      // F6 - Run C++ project
      if (event.key === 'F6') {
        event.preventDefault();
        setShowCppRunner(true);
      }
      // F7 - Run current project runtime
      if (event.key === 'F7') {
        event.preventDefault();
        void openCurrentProjectRuntime();
      }
      if (event.key === 'F9') {
        event.preventDefault();
        buildProject();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [buildProject, openCurrentProjectRuntime]);

  // Apply settings to Monaco editor when settings change
  useEffect(() => {
    if (editorRef.current) {
      const editor = editorRef.current;
      const model = editor.getModel();
      
      console.log('Applying editor settings:', editorSettings);
      console.log('showLineNumbers:', editorSettings.showLineNumbers);
      console.log('lineNumbers setting:', editorSettings.lineNumbers);
      
      // Determine the correct line numbers setting
      const lineNumbersSetting = editorSettings.showLineNumbers ? editorSettings.lineNumbers : 'off';
      console.log('Final lineNumbers setting:', lineNumbersSetting);
      
      // Update editor options
      editor.updateOptions({
        fontSize: editorSettings.fontSize,
        fontFamily: editorSettings.fontFamily,
        fontWeight: editorSettings.fontWeight,
        lineHeight: editorSettings.lineHeight,
        letterSpacing: editorSettings.letterSpacing,
        wordWrap: editorSettings.wordWrap,
        wordWrapColumn: editorSettings.wordWrapColumn,
        minimap: { enabled: editorSettings.minimap, scale: editorSettings.minimapScale },
        // Properly handle line numbers - if showLineNumbers is false, force to 'off'
        lineNumbers: lineNumbersSetting,
        lineNumbersMinChars: editorSettings.lineNumbersMinChars,
        cursorStyle: editorSettings.cursorStyle,
        cursorBlinking: editorSettings.cursorBlinking,
        tabSize: editorSettings.tabSize,
        insertSpaces: editorSettings.insertSpaces,
        detectIndentation: editorSettings.detectIndentation,
        trimAutoWhitespace: editorSettings.trimAutoWhitespace,
        // Properly handle folding controls
        folding: editorSettings.showFoldingControls !== 'never',
        foldingStrategy: 'indentation',
        showFoldingControls: editorSettings.showFoldingControls,
        // Handle whitespace rendering
        renderWhitespace: editorSettings.showWhitespace,
        renderControlCharacters: editorSettings.renderControlCharacters,
        renderIndentGuides: editorSettings.renderIndentGuides,
        highlightActiveIndentGuide: editorSettings.highlightActiveIndentGuide,
        scrollBeyondLastLine: editorSettings.scrollBeyondLastLine,
        smoothScrolling: editorSettings.smoothScrolling,
        autoClosingBrackets: editorSettings.autoClosingBrackets,
        autoClosingQuotes: editorSettings.autoClosingQuotes,
        autoSurround: editorSettings.autoSurround,
        // Additional Monaco options to ensure all settings work
        automaticLayout: true,
        contextmenu: true,
        dragAndDrop: true,
        find: {
          seedSearchStringFromSelection: 'always',
          autoFindInSelection: 'never'
        },
        formatOnPaste: true,
        formatOnType: true,
        links: true,
        mouseWheelZoom: true,
        multiCursorModifier: 'ctrlCmd',
        quickSuggestions: true,
        selectOnLineNumbers: true,
        selectionClipboard: false,
        showUnused: true,
        suggest: {
          showKeywords: true,
          showSnippets: true
        },
        wordBasedSuggestions: 'currentDocument'
      });

      // Update model options if available
      if (model) {
        model.updateOptions({
          tabSize: editorSettings.tabSize,
          insertSpaces: editorSettings.insertSpaces,
          detectIndentation: editorSettings.detectIndentation,
          trimAutoWhitespace: editorSettings.trimAutoWhitespace,
        });
      }

      // Apply theme with actual custom themes
      const getMonacoTheme = (theme: string) => {
        switch (theme) {
          case 'light':
          case 'github-light':
          case 'solarized-light':
            return 'vs';
          case 'high-contrast-light':
            return 'vs';
          case 'high-contrast-dark':
            return 'hc-black';
          case 'cobalt':
          case 'night-blue':
            return 'cobalt';
          case 'night-owl':
            return 'night-owl';
          case 'dracula':
            return 'dracula';
          case 'monokai':
            return 'monokai';
          case 'abyss':
            return 'abyss';
          case 'winter-is-coming':
            return 'winter-is-coming';
          case 'dark':
          case 'github-dark':
          case 'one-dark-pro':
          case 'material-dark':
          case 'solarized-dark':
          default:
            return 'vs-dark';
        }
      };
      
      const monacoTheme = getMonacoTheme(editorSettings.theme);
      editor.updateOptions({ theme: monacoTheme });
    }
  }, [editorSettings]);

  // Load file explorer
  const loadFileExplorer = useCallback(() => {
    if (!currentProject) return;
    const filesInCurrentPath = listFiles(currentPath || currentProject.path);
    setFileItems(filesInCurrentPath);
  }, [currentPath, currentProject]); // Remove listFiles dependency

  // Open file function
  const openFile = useCallback(async (item: UserFile) => {
    if (item.is_directory) {
      setCurrentPath(item.file_path);
      return;
    }

    // Check if file is already open
    const existingTab = tabs.find(tab => tab.path === item.file_path);
    if (existingTab) {
      setActiveTabId(existingTab.id);
      return;
    }

    try {
      // Ensure we load the freshest content for this path
      await reloadFile(item.file_path);
      const fileData = getFile(item.file_path);
      const content = fileData?.content ?? item.content ?? '';
      const newTab: FileTab = {
        id: `tab-${Date.now()}-${Math.random()}`,
        name: item.file_name,
        path: item.file_path,
        content,
        isDirty: false,
        language: getLanguageFromExtension(item.file_name),
      };

      setTabs(prev => [...prev, newTab]);
      setActiveTabId(newTab.id);

      toast({
        title: "File opened",
        description: `${item.file_name} is now open in the editor`,
      });
    } catch (error) {
      toast({
        title: "Error",
        description: `Failed to open ${item.file_name}`,
        variant: "destructive",
      });
    }
  }, [tabs, getFile, reloadFile, toast]);

  // Create a new Untitled file tab
  const createNewFile = useCallback(() => {
    // Determine next Untitled index
    const untitledPrefix = 'Untitled';
    const existingNames = new Set(tabs.map((t) => t.name));
    let index = 1;
    let name = untitledPrefix;
    while (existingNames.has(name)) {
      index += 1;
      name = `${untitledPrefix}-${index}`;
    }

    const newTab: FileTab = {
      id: `tab-${Date.now()}-${Math.random()}`,
      name,
      path: '',
      content: '',
      isDirty: true,
      language: 'plaintext',
    };
    setTabs((prev) => [...prev, newTab]);
    setActiveTabId(newTab.id);
  }, [tabs]);

  useEffect(() => {
    if (currentProject) {
      setCurrentPath(currentProject.path);
      
      // Load file explorer for the project
      const filesInCurrentPath = listFiles(currentProject.path);
      setFileItems(filesInCurrentPath);
      
      // Auto-open main files when project is selected
      const mainFiles = ['main.c', 'App.js', 'index.html', 'main.tsx', 'App.tsx'];
      const projectFiles = filesInCurrentPath;
      
      // Find and open the main file
      const mainFile = projectFiles.find(file => 
        !file.is_directory && mainFiles.includes(file.file_name)
      );
      
      if (mainFile && !tabs.find(tab => tab.path === mainFile.file_path)) {
        openFile(mainFile);
      }
    }
  }, [currentProject]); // Remove listFiles, tabs, openFile dependencies

  // Get language from file extension
  const getLanguageFromExtension = (filename: string): string => {
    const ext = filename.split('.').pop()?.toLowerCase();
    const languageMap: Record<string, string> = {
      'js': 'javascript',
      'jsx': 'javascript',
      'ts': 'typescript',
      'tsx': 'typescript',
      'py': 'python',
      'java': 'java',
      'cpp': 'cpp',
      'c': 'c',
      'cs': 'csharp',
      'php': 'php',
      'html': 'html',
      'css': 'css',
      'scss': 'scss',
      'json': 'json',
      'xml': 'xml',
      'sql': 'sql',
      'sh': 'shell',
      'md': 'markdown',
      'yaml': 'yaml',
      'yml': 'yaml',
      'toml': 'toml',
      'ini': 'ini',
      'cfg': 'ini',
      'txt': 'plaintext',
    };
    return languageMap[ext || ''] || 'plaintext';
  };
  

  // Save current file
  const saveCurrentFile = async () => {
    const activeTab = tabs.find(tab => tab.id === activeTabId);
    if (!activeTab) return;

    try {
      const ok = await updateFile(activeTab.path, activeTab.content);
      if (!ok) {
        throw new Error('VFS update failed');
      }
      setTabs(prev => prev.map(tab => 
        tab.id === activeTabId ? { ...tab, isDirty: false } : tab
      ));

      toast({
        title: "File saved",
        description: `${activeTab.name} has been saved`,
      });
    } catch (error) {
      toast({
        title: "Error",
        description: `Failed to save ${activeTab.name}`,
        variant: "destructive",
      });
    }
  };

  // Helper: save a specific tab by id
  const saveTabById = async (tabId: string) => {
    const t = tabs.find(tb => tb.id === tabId);
    if (!t) return false;
    if (!t.path) {
      // If untitled, trigger Save As flow
      setShowSaveDialog(true);
      return false;
    }
    const ok = await updateFile(t.path, t.content);
    if (ok) {
      setTabs(prev => prev.map(tb => tb.id === tabId ? { ...tb, isDirty: false } : tb));
      toast({ title: 'Saved', description: t.name });
      return true;
    } else {
      toast({ title: 'Error', description: `Failed to save ${t.name}`, variant: 'destructive' });
      return false;
    }
  };

  // Save all tabs
  const saveAllTabs = async () => {
    for (const t of tabs) {
      if (t.isDirty && t.path) {
        await updateFile(t.path, t.content);
      }
    }
    setTabs(prev => prev.map(tb => ({ ...tb, isDirty: false })));
    toast({ title: 'All saved', description: 'Saved all modified files' });
  };

  // Close tab
  const closeTab = (tabId: string) => {
    const tabToClose = tabs.find(tab => tab.id === tabId);
    if (tabToClose?.isDirty) {
      if (!confirm(`${tabToClose.name} has unsaved changes. Close anyway?`)) {
        return;
      }
    }

    const newTabs = tabs.filter(tab => tab.id !== tabId);
    setTabs(newTabs);

    if (activeTabId === tabId) {
      setActiveTabId(newTabs.length > 0 ? newTabs[newTabs.length - 1].id : '');
    }
  };

  // Close other tabs
  const closeOtherTabs = (tabId: string) => {
    setTabs(prev => prev.filter(t => t.id === tabId));
    setActiveTabId(tabId);
  };

  // Close tabs to the right
  const closeTabsToRight = (tabId: string) => {
    setTabs(prev => {
      const idx = prev.findIndex(t => t.id === tabId);
      if (idx < 0) return prev;
      return prev.slice(0, idx + 1);
    });
    setActiveTabId(tabId);
  };

  // Reorder tabs from ordered id list
  const reorderTabs = (orderedIds: string[]) => {
    setTabs(prev => {
      const map = new Map(prev.map(t => [t.id, t]));
      return orderedIds.map(id => map.get(id)!).filter(Boolean);
    });
  };

  // Handle editor change
  const handleEditorChange = (value: string | undefined) => {
    if (!activeTabId || value === undefined) return;

    setTabs(prev => prev.map(tab => 
      tab.id === activeTabId 
        ? { ...tab, content: value, isDirty: true }
        : tab
    ));
  };

  // Handle editor mount
  const handleEditorDidMount = (editor: any, monaco: any) => {
    editorRef.current = editor;
    
    // Define custom themes
    monaco.editor.defineTheme('cobalt', {
      base: 'vs-dark',
      inherit: true,
      rules: [
        { token: 'comment', foreground: '628BB8', fontStyle: 'italic' },
        { token: 'keyword', foreground: 'FF9D00' },
        { token: 'operator', foreground: 'FFFFFF' },
        { token: 'namespace', foreground: 'FFEE99' },
        { token: 'type', foreground: '80FFBB' },
        { token: 'struct', foreground: '80FFBB' },
        { token: 'class', foreground: '80FFBB' },
        { token: 'interface', foreground: '80FFBB' },
        { token: 'parameter', foreground: 'FFC600' },
        { token: 'variable', foreground: 'FFFFFF' },
        { token: 'string', foreground: '3AD900' },
        { token: 'number', foreground: 'FF0080' },
        { token: 'regexp', foreground: 'FF0080' },
        { token: 'delimiter', foreground: 'FFEE99' }
      ],
      colors: {
        'editor.background': '#002240',
        'editor.foreground': '#FFFFFF',
        'editorLineNumber.foreground': '#628BB8',
        'editorCursor.foreground': '#FFFFFF',
        'editor.selectionBackground': '#B36539BF',
        'editor.lineHighlightBackground': '#00364D'
      }
    });

    monaco.editor.defineTheme('night-owl', {
      base: 'vs-dark',
      inherit: true,
      rules: [
        { token: 'comment', foreground: '637777', fontStyle: 'italic' },
        { token: 'keyword', foreground: 'C792EA' },
        { token: 'operator', foreground: 'C792EA' },
        { token: 'type', foreground: 'FFCB8B' },
        { token: 'class', foreground: 'FFCB8B' },
        { token: 'interface', foreground: 'FFCB8B' },
        { token: 'parameter', foreground: 'D6DEEB' },
        { token: 'variable', foreground: 'D6DEEB' },
        { token: 'string', foreground: 'ECC48D' },
        { token: 'number', foreground: 'F78C6C' },
        { token: 'regexp', foreground: '5CA7E4' },
        { token: 'delimiter', foreground: 'D6DEEB' }
      ],
      colors: {
        'editor.background': '#011627',
        'editor.foreground': '#D6DEEB',
        'editorLineNumber.foreground': '#4B6479',
        'editorCursor.foreground': '#80A4C2',
        'editor.selectionBackground': '#1D3B53',
        'editor.lineHighlightBackground': '#010E17'
      }
    });

    monaco.editor.defineTheme('dracula', {
      base: 'vs-dark',
      inherit: true,
      rules: [
        { token: 'comment', foreground: '6272A4', fontStyle: 'italic' },
        { token: 'keyword', foreground: 'FF79C6' },
        { token: 'operator', foreground: 'FF79C6' },
        { token: 'type', foreground: '8BE9FD' },
        { token: 'class', foreground: '50FA7B' },
        { token: 'interface', foreground: '50FA7B' },
        { token: 'parameter', foreground: 'FFB86C' },
        { token: 'variable', foreground: 'F8F8F2' },
        { token: 'string', foreground: 'F1FA8C' },
        { token: 'number', foreground: 'AE81FF' },
        { token: 'regexp', foreground: 'F1FA8C' },
        { token: 'delimiter', foreground: 'F8F8F2' }
      ],
      colors: {
        'editor.background': '#282A36',
        'editor.foreground': '#F8F8F2',
        'editorLineNumber.foreground': '#6272A4',
        'editorCursor.foreground': '#F8F8F0',
        'editor.selectionBackground': '#44475A',
        'editor.lineHighlightBackground': '#3E3D32'
      }
    });

    monaco.editor.defineTheme('monokai', {
      base: 'vs-dark',
      inherit: true,
      rules: [
        { token: 'comment', foreground: '75715E', fontStyle: 'italic' },
        { token: 'keyword', foreground: 'F92672' },
        { token: 'operator', foreground: 'F92672' },
        { token: 'type', foreground: '66D9EF' },
        { token: 'class', foreground: 'A6E22E' },
        { token: 'interface', foreground: 'A6E22E' },
        { token: 'parameter', foreground: 'FD971F' },
        { token: 'variable', foreground: 'F8F8F2' },
        { token: 'string', foreground: 'E6DB74' },
        { token: 'number', foreground: 'AE81FF' },
        { token: 'regexp', foreground: 'E6DB74' },
        { token: 'delimiter', foreground: 'F8F8F2' }
      ],
      colors: {
        'editor.background': '#272822',
        'editor.foreground': '#F8F8F2',
        'editorLineNumber.foreground': '#90908A',
        'editorCursor.foreground': '#F8F8F0',
        'editor.selectionBackground': '#49483E',
        'editor.lineHighlightBackground': '#3E3D32'
      }
    });

    monaco.editor.defineTheme('abyss', {
      base: 'vs-dark',
      inherit: true,
      rules: [
        { token: 'comment', foreground: '6688CC', fontStyle: 'italic' },
        { token: 'keyword', foreground: 'DDA0DD' },
        { token: 'operator', foreground: 'DDA0DD' },
        { token: 'type', foreground: '559BD4' },
        { token: 'class', foreground: '4EC9B0' },
        { token: 'interface', foreground: '4EC9B0' },
        { token: 'parameter', foreground: '9CDCFE' },
        { token: 'variable', foreground: '9CDCFE' },
        { token: 'string', foreground: 'CE9178' },
        { token: 'number', foreground: 'B5CEA8' },
        { token: 'regexp', foreground: 'D16969' },
        { token: 'delimiter', foreground: 'D4D4D4' }
      ],
      colors: {
        'editor.background': '#000C18',
        'editor.foreground': '#6688CC',
        'editorLineNumber.foreground': '#2F4F4F',
        'editorCursor.foreground': '#AEAFAD',
        'editor.selectionBackground': '#0E639C50',
        'editor.lineHighlightBackground': '#001122'
      }
    });

    monaco.editor.defineTheme('winter-is-coming', {
      base: 'vs-dark',
      inherit: true,
      rules: [
        { token: 'comment', foreground: '6B7D8F', fontStyle: 'italic' },
        { token: 'keyword', foreground: '5ABEB0' },
        { token: 'operator', foreground: '5ABEB0' },
        { token: 'type', foreground: '8FBFDC' },
        { token: 'class', foreground: 'F4D03F' },
        { token: 'interface', foreground: 'F4D03F' },
        { token: 'parameter', foreground: 'C3E88D' },
        { token: 'variable', foreground: 'E6EDF3' },
        { token: 'string', foreground: 'C3E88D' },
        { token: 'number', foreground: 'F78C6C' },
        { token: 'regexp', foreground: 'C3E88D' },
        { token: 'delimiter', foreground: 'E6EDF3' }
      ],
      colors: {
        'editor.background': '#0D1421',
        'editor.foreground': '#E6EDF3',
        'editorLineNumber.foreground': '#4A5568',
        'editorCursor.foreground': '#FFFFFF',
        'editor.selectionBackground': '#1E293B',
        'editor.lineHighlightBackground': '#1E293B50'
      }
    });

    // Track cursor position changes
    editor.onDidChangeCursorPosition((e: any) => {
      setCursorPosition({
        line: e.position.lineNumber,
        column: e.position.column
      });
    });

    // Track selection changes
    editor.onDidChangeCursorSelection((e: any) => {
      const selection = editor.getSelection();
      if (selection) {
        const selectedText = editor.getModel()?.getValueInRange(selection) || '';
        const selectedLines = selectedText.split('\n').length - 1;
        setSelection({
          selectedChars: selectedText.length,
          selectedLines: selectedLines > 0 ? selectedLines : 0
        });
      } else {
        setSelection({ selectedChars: 0, selectedLines: 0 });
      }
    });
    
    // Add keyboard shortcuts
    editor.addAction({
      id: 'save-file',
      label: 'Save File',
      keybindings: [
        monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyS
      ],
      run: () => {
        if (tabs.find(tab => tab.id === activeTabId)?.path) {
          saveCurrentFile();
        } else {
          setShowSaveDialog(true);
        }
      }
    });
  };

  const navigateToPath = (path: string) => {
    if (currentProject && path.startsWith(currentProject.path)) {
      setCurrentPath(path);
    }
  };

  const handleProjectSelect = (project: Project) => {
    setCurrentProject(project);
    setCurrentPath(project.path);
    setShowProjectManager(false);
    
    // Close any existing tabs when switching projects
    setTabs([]);
    setActiveTabId('');
  };

  const navigateUp = () => {
    if (!currentProject) return;
    if (currentPath === currentProject.path) return;
    
    const pathParts = currentPath.split('\\');
    pathParts.pop();
    const parentPath = pathParts.join('\\');
    
    if (parentPath.length >= currentProject.path.length) {
      setCurrentPath(parentPath);
    }
  };

  // Export project functionality
  const exportProject = async () => {
    if (!currentProject) {
      toast({
        title: "No project",
        description: "Please open a project first",
        variant: "destructive",
      });
      return;
    }

    try {
      // Get all files in the project
      const allFiles = listFiles(currentProject.path);
      
      // Create a simple export format (we'll simulate zip functionality)
      const exportData = {
        project: currentProject,
        files: allFiles.map(file => ({
          ...file,
          content: file.is_directory ? null : getFile(file.file_path)?.content || ''
        }))
      };

      const dataStr = JSON.stringify(exportData, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      
      saveAs(dataBlob, `${currentProject.name}.project.json`);
      
      toast({
        title: "Project exported",
        description: `${currentProject.name} has been exported successfully`,
      });
    } catch (error) {
      toast({
        title: "Export failed",
        description: "Failed to export the project",
        variant: "destructive",
      });
    }
  };

  // Import project functionality
  const handleImportProject = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      const text = await file.text();
      const importData = JSON.parse(text);
      
      if (!importData.project || !importData.files) {
        throw new Error('Invalid project file format');
      }

      const project = importData.project;
      const files = importData.files;

      // Create project directory structure
      for (const fileItem of files) {
        if (fileItem.is_directory) {
          // Create directory (handled by createFile when creating files in subdirectories)
          continue;
        } else {
          // Create file with content
          await createFile(fileItem.file_path, fileItem.content || '');
        }
      }

      // Set the imported project as current
      setCurrentProject(project);
      setCurrentPath(project.path);
      setShowImportDialog(false);
      
      // Close existing tabs
      setTabs([]);
      setActiveTabId('');

      toast({
        title: "Project imported",
        description: `${project.name} has been imported successfully`,
      });
    } catch (error) {
      toast({
        title: "Import failed",
        description: "Failed to import the project. Please check the file format.",
        variant: "destructive",
      });
    }

    // Reset file input
    event.target.value = '';
  };

  // Load available projects
  const loadAvailableProjects = async () => {
    try {
      const allFiles = listFiles('');
      console.log('All files found:', allFiles.map(f => f.file_path));
      
      const projectFiles = allFiles.filter(file => 
        file.file_name === '.project' && !file.is_directory
      );
      console.log('Project files found:', projectFiles.map(f => f.file_path));
      
      const projects: Project[] = [];
      for (const projectFile of projectFiles) {
        try {
          const fileData = getFile(projectFile.file_path);
          console.log('Project file data:', projectFile.file_path, fileData);
          if (fileData?.content) {
            const projectData = JSON.parse(fileData.content);
            console.log('Parsed project data:', projectData);
            projects.push({
              id: projectData.id,
              name: projectData.name,
              path: projectFile.parent_path || '',
              type: projectData.type,
              description: projectData.description,
              created: projectData.created,
              lastModified: projectFile.updated_at
            });
          }
        } catch (error) {
          console.error('Failed to parse project file:', projectFile.file_path, error);
        }
      }
      
      setAvailableProjects(projects);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to load available projects",
        variant: "destructive",
      });
    }
  };

  // Handle opening project dialog
  const handleOpenProjectDialog = () => {
    loadAvailableProjects();
    setShowOpenProjectDialog(true);
  };

  // Handle selecting a project to open
  const handleOpenProject = (project: Project) => {
    setCurrentProject(project);
    setCurrentPath(project.path);
    setShowOpenProjectDialog(false);
    
    // Close any existing tabs when opening a project
    setTabs([]);
    setActiveTabId('');
    
    toast({
      title: "Project opened",
      description: `${project.name} is now open`,
    });
  };

  // Editor actions with monaco commands
  const triggerEditorAction = (action: string) => {
    if (editorRef.current) {
      editorRef.current.trigger('keyboard', action, null);
    }
  };

  const activeTab = tabs.find(tab => tab.id === activeTabId);

  const getLanguageFromPath = (path: string): string => {
    const ext = path.split('.').pop()?.toLowerCase();
    switch (ext) {
      case 'jsx':
      case 'js':
        return 'javascript';
      case 'tsx':
      case 'ts':
        return 'typescript';
      case 'html':
        return 'html';
      case 'css':
        return 'css';
      case 'json':
        return 'json';
      case 'md':
        return 'markdown';
      case 'cpp':
      case 'c':
        return 'cpp';
      default:
        return 'javascript';
    }
  };

  useEffect(() => {
    if (!currentProject) return;

    const ensureModuleSources = async () => {
      // Ensure directories exist (safe no-ops if present)
      const srcDir = `${currentProject.path}\\src`;
      const buildDir = `${currentProject.path}\\build`;
      try { await ensureDir(srcDir); } catch {}
      try { await ensureDir(buildDir); } catch {}

      // Default AssemblyScript source
      const mainPath = `${currentProject.path}\\src\\main.ts`;
      const defaultMain = `// AssemblyScript sample for MapleOS Module
// Compile target: build/module.wasm (WAT will be compiled by Module Host)
export function add(a: i32, b: i32): i32 { return a + b; }
`;

      if (!fileExists(mainPath)) {
        await createFile(mainPath, defaultMain);
        toast({ title: "Added source", description: "Created src/main.ts" });
      } else {
        const mainExisting = getFile(mainPath)?.content || '';
        if (!mainExisting.trim()) {
          await updateFile(mainPath, defaultMain);
          toast({ title: "Repaired source", description: "Filled src/main.ts with default content" });
        }
      }

      // Default WAT (stored in .wasm as WAT text for Module Host to compile)
      const wasmPath = `${currentProject.path}\\build\\module.wasm`;
      const defaultWAT = `(module
  (func $add (param i32 i32) (result i32)
    local.get 0
    local.get 1
    i32.add)
  (export "add" (func $add))
)`;

      if (!fileExists(wasmPath)) {
        await createFile(wasmPath, defaultWAT);
      } else {
        const wasmExisting = getFile(wasmPath)?.content || '';
        if (!wasmExisting.trim()) {
          await updateFile(wasmPath, defaultWAT);
        }
      }

      // Refresh explorer so new files appear
      loadFileExplorer();
    };

    void ensureModuleSources();
  }, [currentProject, fileExists, createFile, updateFile, getFile, loadFileExplorer, toast]);

  // Map extensions to content types
  const getContentTypeFromExt = (ext: string): string => {
    switch (ext) {
      case 'json': return 'application/json';
      case 'md': return 'text/markdown';
      case 'html': return 'text/html';
      case 'css':
      case 'scss': return 'text/css';
      case 'wat': return 'application/wat';
      case 'wasm': return 'application/wasm';
      case 'ts':
      case 'tsx':
      case 'js':
      case 'jsx':
      case 'c':
      case 'cpp':
      case 'h':
      case 'java':
      case 'py':
      default: return 'text/plain';
    }
  };

  // Save As handler for SaveAsDialog
  const handleSaveAs = async (fullPath: string, finalFileName: string) => {
    const activeTab = tabs.find(t => t.id === activeTabId);
    if (!activeTab) return;

    const parent = fullPath.includes('\\') ? fullPath.substring(0, fullPath.lastIndexOf('\\')) : '';
    // Ensure parent directories exist in Supabase VFS
    if (parent) {
      const parts = parent.split('\\').filter(Boolean);
      let acc = parts[0] || '';
      for (let i = 1; i < parts.length; i++) {
        acc = `${acc}\\${parts[i]}`;
        if (!fileExists(acc)) {
          // Create directory records
          await createFile(acc, '', true, 'directory');
        }
      }
      // Also ensure the final parent exists (in case only two parts)
      if (!fileExists(parent)) {
        await createFile(parent, '', true, 'directory');
      }
    }

    const ext = finalFileName.split('.').pop()?.toLowerCase() || '';
    const contentType = getContentTypeFromExt(ext);

    const exists = fileExists(fullPath);
    const ok = exists
      ? await updateFile(fullPath, activeTab.content)
      : await createFile(fullPath, activeTab.content, false, contentType);

    if (!ok) {
      toast({ title: 'Save failed', description: `Could not save ${finalFileName}`, variant: 'destructive' });
      return;
    }

    // Update the active tab to reflect the new file path/name/language and mark clean
    setTabs(prev => prev.map(t => t.id === activeTabId
      ? { ...t, name: finalFileName, path: fullPath, isDirty: false, language: getLanguageFromExtension(finalFileName) }
      : t
    ));

    // Refresh explorer and close dialog
    if (parent) setCurrentPath(parent);
    loadFileExplorer();
    setShowSaveDialog(false);

    toast({ title: 'File saved', description: `${finalFileName} saved successfully` });
  };

  return (
    <div className="h-full bg-background flex flex-col">
      {/* Menu Bar */}
      <div className="bg-background border-b border-border">
        <Menubar className="rounded-none border-none h-8">
          <MenubarMenu>
            <MenubarTrigger className="px-3 py-1 h-8">File</MenubarTrigger>
            <MenubarContent className="z-[10000]">
              <MenubarItem onClick={() => setShowProjectManager(true)}>
                <FolderOpen className="w-4 h-4 mr-2" />
                Open Project...
              </MenubarItem>
              <MenubarItem onClick={handleOpenProjectDialog}>
                <FolderPlus className="w-4 h-4 mr-2" />
                New Project...
              </MenubarItem>
              <MenubarItem onClick={createNewFile}>
                <FileIcon className="w-4 h-4 mr-2" />
                New File
              </MenubarItem>
              <MenubarSeparator />
              <MenubarItem 
                onClick={() => {
                  if (activeTab?.path) {
                    saveCurrentFile();
                  } else {
                    setShowSaveDialog(true);
                  }
                }}
                disabled={!activeTab}
              >
                <Save className="w-4 h-4 mr-2" />
                Save
                <span className="ml-auto text-xs text-muted-foreground">Ctrl+S</span>
              </MenubarItem>
              <MenubarItem 
                onClick={() => setShowSaveDialog(true)}
                disabled={!activeTab}
              >
                <Save className="w-4 h-4 mr-2" />
                Save As...
              </MenubarItem>
              <MenubarSeparator />
              <MenubarItem onClick={exportProject} disabled={!currentProject}>
                <Download className="w-4 h-4 mr-2" />
                Export Project
              </MenubarItem>
              <MenubarItem onClick={() => setShowImportDialog(true)}>
                <Upload className="w-4 h-4 mr-2" />
                Import Project
              </MenubarItem>
            </MenubarContent>
          </MenubarMenu>

          <MenubarMenu>
            <MenubarTrigger className="px-3 py-1 h-8">Edit</MenubarTrigger>
            <MenubarContent className="z-[10000]">
              <MenubarItem onClick={() => triggerEditorAction('undo')}>
                <Undo2 className="w-4 h-4 mr-2" />
                Undo
                <span className="ml-auto text-xs text-muted-foreground">Ctrl+Z</span>
              </MenubarItem>
              <MenubarItem onClick={() => triggerEditorAction('redo')}>
                <RotateCw className="w-4 h-4 mr-2" />
                Redo
                <span className="ml-auto text-xs text-muted-foreground">Ctrl+Y</span>
              </MenubarItem>
              <MenubarSeparator />
              <MenubarItem onClick={() => triggerEditorAction('editor.action.clipboardCutAction')}>
                <Scissors className="w-4 h-4 mr-2" />
                Cut
                <span className="ml-auto text-xs text-muted-foreground">Ctrl+X</span>
              </MenubarItem>
              <MenubarItem onClick={() => triggerEditorAction('editor.action.clipboardCopyAction')}>
                <Copy className="w-4 h-4 mr-2" />
                Copy
                <span className="ml-auto text-xs text-muted-foreground">Ctrl+C</span>
              </MenubarItem>
              <MenubarItem onClick={() => triggerEditorAction('editor.action.clipboardPasteAction')}>
                <ClipboardPaste className="w-4 h-4 mr-2" />
                Paste
                <span className="ml-auto text-xs text-muted-foreground">Ctrl+V</span>
              </MenubarItem>
            </MenubarContent>
          </MenubarMenu>

          <MenubarMenu>
            <MenubarTrigger className="px-3 py-1 h-8">View</MenubarTrigger>
            <MenubarContent className="z-[10000]">
               <MenubarItem onClick={() => setShowFileExplorer(!showFileExplorer)}>
                 <FolderOpen className="w-4 h-4 mr-2" />
                 {showFileExplorer ? 'Hide' : 'Show'} Explorer
               </MenubarItem>
               <MenubarSeparator />
               <MenubarItem onClick={() => {
                 if (onOpenWindow) {
                   onOpenWindow('editor-settings', 'Editor Settings');
                 }
               }}>
                 <Settings className="w-4 h-4 mr-2" />
                 Settings...
               </MenubarItem>
            </MenubarContent>
          </MenubarMenu>

          <MenubarMenu>
            <MenubarTrigger className="px-3 py-1 h-8">Run</MenubarTrigger>
            <MenubarContent className="z-[10000]">
              <MenubarItem onClick={() => setShowRunner(true)}>
                <Play className="w-4 h-4 mr-2" />
                Run React Project
                <span className="ml-auto text-xs text-muted-foreground">F5</span>
              </MenubarItem>
              <MenubarItem onClick={() => setShowCppRunner(true)}>
                <Gamepad2 className="w-4 h-4 mr-2" />
                Run C++ Project
                <span className="ml-auto text-xs text-muted-foreground">F6</span>
              </MenubarItem>
              <MenubarItem onClick={() => {
                void openCurrentProjectRuntime();
              }}>
                <Cpu className="w-4 h-4 mr-2" />
                Run Module
                <span className="ml-auto text-xs text-muted-foreground">F7</span>
              </MenubarItem>
              <MenubarSeparator />
              <MenubarItem onClick={buildProject} disabled={isBuilding}>
                <Hammer className="w-4 h-4 mr-2" />
                Build (WAT → WASM)
                <span className="ml-auto text-xs text-muted-foreground">F9</span>
              </MenubarItem>
            </MenubarContent>
          </MenubarMenu>

          <MenubarMenu>
            <MenubarTrigger className="px-3 py-1 h-8">Help</MenubarTrigger>
            <MenubarContent className="z-[10000]">
              <MenubarItem>
                <HelpCircle className="w-4 h-4 mr-2" />
                Documentation
              </MenubarItem>
              <MenubarItem>
                <ExternalLink className="w-4 h-4 mr-2" />
                GitHub Repository
              </MenubarItem>
              <MenubarSeparator />
              <MenubarItem onClick={() => setShowAboutDialog(true)}>
                <Info className="w-4 h-4 mr-2" />
                About Maple Code Editor
              </MenubarItem>
            </MenubarContent>
          </MenubarMenu>
        </Menubar>
      </div>

      {/* VS Code–style Tab Bar */}
      <EditorTabBar
        tabs={tabs.map(t => ({ id: t.id, name: t.name, path: t.path, isDirty: t.isDirty }))}
        activeId={activeTabId || null}
        onSelect={(id) => setActiveTabId(id)}
        onClose={closeTab}
        onReorder={reorderTabs}
        onNewUntitled={createNewFile}
        onCloseOthers={closeOtherTabs}
        onCloseRight={closeTabsToRight}
        onSave={saveTabById}
        onSaveAll={saveAllTabs}
        onRevealInExplorer={(id) => {
          const t = tabs.find(tb => tb.id === id);
          if (!t?.path) return;
          const parent = t.path.includes('\\') ? t.path.substring(0, t.path.lastIndexOf('\\')) : currentProject?.path || '';
          if (parent) setCurrentPath(parent);
          toast({ title: 'Revealed in Explorer', description: parent });
        }}
        onCopyPath={(id) => {
          const t = tabs.find(tb => tb.id === id);
          if (!t?.path) return;
          navigator.clipboard?.writeText(t.path);
          toast({ title: 'Path copied', description: t.path });
        }}
      />

      {/* Enhanced Footer Status Bar */}
      <div className="bg-accent border-b border-border px-3 py-1 flex items-center justify-between text-xs">
        <div className="flex items-center gap-6">
          {/* Project Info */}
          <div className="flex items-center gap-2">
            <Code className="w-3 h-3 text-primary" />
            <span className="font-medium">Maple Code</span>
            {currentProject && (
              <>
                <span className="text-muted-foreground">•</span>
                <div className="flex items-center gap-1 text-muted-foreground">
                  <Folder className="w-3 h-3" />
                  <span>{currentProject.name}</span>
                </div>
              </>
            )}
          </div>

          {/* File Info */}
          {activeTab && (
            <>
              <div className="flex items-center gap-4 text-muted-foreground">
                <span className="flex items-center gap-1">
                  <FileText className="w-3 h-3" />
                  {activeTab.name}
                  {activeTab.isDirty && <span className="text-amber-500">•</span>}
                </span>
                <span>{activeTab.language}</span>
                <span>UTF-8</span>
                <span>LF</span>
              </div>
            </>
          )}
        </div>

        <div className="flex items-center gap-6 text-muted-foreground">
          {activeTab && (
            <>
              {/* Selection Info */}
              {selection.selectedChars > 0 && (
                <span>
                  ({selection.selectedChars} chars, {selection.selectedLines} lines selected)
                </span>
              )}
              
              {/* Line Count */}
              <span>{activeTab.content.split('\n').length} lines</span>
              
              {/* Cursor Position */}
              <span>Ln {cursorPosition.line}, Col {cursorPosition.column}</span>
              
              {/* File Size */}
              <span>{new Blob([activeTab.content]).size} bytes</span>
              
              {/* Indentation */}
              <span>Spaces: 2</span>
            </>
          )}
          
          {/* Editor Settings Indicators */}
          <div className="flex items-center gap-2">
            {editorSettings.wordWrap === 'on' && (
              <span className="text-xs bg-primary/10 text-primary px-1 rounded">Wrap</span>
            )}
            {editorSettings.minimap && (
              <span className="text-xs bg-primary/10 text-primary px-1 rounded">Map</span>
            )}
          </div>
        </div>
      </div>

      <div className="flex-1 flex">
        {/* Sidebar */}
        {showFileExplorer && (
          <div className="w-80 border-r border-border flex flex-col">
            <Tabs defaultValue="explorer" className="h-full flex flex-col">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="explorer">Explorer</TabsTrigger>
                <TabsTrigger value="search">Search</TabsTrigger>
              </TabsList>
              
              <TabsContent value="explorer" className="flex-1 flex flex-col m-0">
                <FileTreeExplorer
                  currentProject={currentProject}
                  currentPath={currentPath}
                  onFileSelect={openFile}
                  onNavigate={navigateToPath}
                  onRefresh={loadFileExplorer}
                />
              </TabsContent>

              <TabsContent value="search" className="flex-1 m-0 p-3">
                <div className="space-y-3">
                  <Input placeholder="Search..." />
                  <Input placeholder="Replace..." />
                  <div className="flex gap-2">
                    <Button size="sm">
                      <Search className="w-4 h-4 mr-1" />
                      Find
                    </Button>
                    <Button size="sm" variant="outline">
                      <Replace className="w-4 h-4 mr-1" />
                      Replace
                    </Button>
                  </div>
                </div>
              </TabsContent>
            </Tabs>
          </div>
        )}

        {/* Main Content */}
        <div className="flex-1 flex flex-col">
          {/* Toolbar */}
          <div className="border-b border-border p-2 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={createNewFile}
              >
                <Plus className="w-4 h-4" />
              </Button>
              
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  if (activeTab?.path) {
                    saveCurrentFile();
                  } else {
                    setShowSaveDialog(true);
                  }
                }}
                disabled={!activeTab || (!activeTab.isDirty && !!activeTab.path)}
              >
                <Save className="w-4 h-4" />
              </Button>
              
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowSaveDialog(true)}
                disabled={!activeTab}
              >
                Save As
              </Button>
              
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowAIBuildPanel(v => !v)}
                title={showAIBuildPanel ? 'Hide AI Builder' : 'Show AI Builder'}
              >
                <Sparkles className="w-4 h-4" />
              </Button>
            </div>

            <div className="text-xs text-muted-foreground">
              {activeTab ? `${activeTab.language}` : 'No file selected'}
            </div>
          </div>

          {/* Editor and AI Builder side-by-side */}
          <div className="flex-1 flex min-h-0">
            <div className={showAIBuildPanel ? "flex-1" : "flex-1"}>
              {activeTab ? (
                <Editor
                  height="100%"
                  language={activeTab.language}
                  value={activeTab.content}
                  theme={theme === 'dark' ? 'vs-dark' : 'light'}
                  onChange={handleEditorChange}
                  onMount={handleEditorDidMount}
                  options={{
                    fontSize: editorSettings.fontSize,
                    wordWrap: editorSettings.wordWrap,
                    minimap: { enabled: editorSettings.minimap },
                    lineNumbers: editorSettings.lineNumbers,
                    automaticLayout: true,
                    scrollBeyondLastLine: false,
                    smoothScrolling: true,
                    cursorSmoothCaretAnimation: "on",
                    suggest: { showKeywords: true, showSnippets: true },
                    quickSuggestions: true,
                    parameterHints: { enabled: true },
                    formatOnPaste: true,
                    formatOnType: true,
                  }}
                />
              ) : (
                <div className="h-full flex items-center justify-center">
                  <div className="text-center">
                    <Code className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
                    <h3 className="text-lg font-semibold mb-2">Welcome to Maple Code Editor</h3>
                    <p className="text-muted-foreground mb-4">
                      Open a file from the explorer or create a new one to start coding
                    </p>
                    <Button onClick={createNewFile}>
                      <Plus className="w-4 h-4 mr-2" />
                      New File
                    </Button>
                  </div>
                </div>
              )}
            </div>

            {showAIBuildPanel && (
              <div className="w-[360px] border-l border-border p-2 bg-background">
                <AIBuildPanel
                  editorRef={editorRef}
                  activeFileName={activeTab?.name}
                  language={activeTab?.language}
                />
              </div>
            )}
          </div>
        </div>
      </div>

      <SaveAsDialog
        isOpen={showSaveDialog}
        onClose={() => setShowSaveDialog(false)}
        onSave={handleSaveAs}
        currentFileName={activeTab?.name || 'Untitled'}
      />

      <Dialog open={showProjectManager} onOpenChange={setShowProjectManager}>
        <DialogContent className="sm:max-w-[800px] h-[600px] z-[9999]">
          <DialogHeader>
            <DialogTitle>Project Manager</DialogTitle>
          </DialogHeader>
          <div className="h-full overflow-hidden">
            <ProjectManager onProjectSelect={handleProjectSelect} currentProject={currentProject} />
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showImportDialog} onOpenChange={setShowImportDialog}>
        <DialogContent className="sm:max-w-[400px] z-[9999]">
          <DialogHeader>
            <DialogTitle>Import Project</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="import-file">Select project file (.project.json)</Label>
              <Input
                id="import-file"
                type="file"
                accept=".json,.project.json"
                onChange={handleImportProject}
                className="mt-2"
              />
            </div>
            <p className="text-sm text-muted-foreground">
              Import a previously exported project file to restore your workspace.
            </p>
          </div>
        </DialogContent>
      </Dialog>

      {/* Microsoft-style About Dialog */}
      <Dialog open={showAboutDialog} onOpenChange={setShowAboutDialog}>
        <DialogContent className="sm:max-w-[500px] z-[9999] overflow-hidden">
          <div className="space-y-6">
            {/* Header with Logo and Title */}
            <div className="flex items-center gap-4 pb-4 border-b border-border">
              <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center">
                <Code className="w-8 h-8 text-white" />
              </div>
              <div>
                <h2 className="text-2xl font-bold">Maple Code Editor</h2>
                <p className="text-sm text-muted-foreground">Professional Code Development Environment</p>
              </div>
            </div>

            {/* Version Information */}
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="font-medium">Version:</span>
                <span className="font-mono text-sm">1.4.2 (Release)</span>
              </div>
              <div className="flex justify-between">
                <span className="font-medium">Build:</span>
                <span className="font-mono text-sm">2024.16.712</span>
              </div>
              <div className="flex justify-between">
                <span className="font-medium">Release Date:</span>
                <span className="text-sm">January 16, 2025</span>
              </div>
              <div className="flex justify-between">
                <span className="font-medium">Edition:</span>
                <span className="text-sm">Community</span>
              </div>
            </div>

            {/* System Information */}
            <div className="space-y-3 pt-4 border-t border-border">
              <h3 className="font-semibold flex items-center gap-2">
                <Monitor className="w-4 h-4" />
                System Information
              </h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span>Platform:</span>
                  <span className="font-mono">{navigator.platform}</span>
                </div>
                <div className="flex justify-between">
                  <span>User Agent:</span>
                  <span className="font-mono text-xs max-w-60 truncate">
                    {navigator.userAgent.split(' ')[0]}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>Memory:</span>
                  <span className="font-mono">
                    {(navigator as any).deviceMemory ? `${(navigator as any).deviceMemory}GB` : 'Unknown'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>CPU Cores:</span>
                  <span className="font-mono">{navigator.hardwareConcurrency || 'Unknown'}</span>
                </div>
              </div>
            </div>

            {/* Features */}
            <div className="space-y-3 pt-4 border-t border-border">
              <h3 className="font-semibold">Features</h3>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="flex items-center gap-2">
                  <Code className="w-3 h-3" />
                  <span>Monaco Editor</span>
                </div>
                <div className="flex items-center gap-2">
                  <FileText className="w-3 h-3" />
                  <span>Multi-language Support</span>
                </div>
                <div className="flex items-center gap-2">
                  <Folder className="w-3 h-3" />
                  <span>Project Management</span>
                </div>
                <div className="flex items-center gap-2">
                  <Download className="w-3 h-3" />
                  <span>Import/Export</span>
                </div>
                <div className="flex items-center gap-2">
                  <Search className="w-3 h-3" />
                  <span>Advanced Search</span>
                </div>
                <div className="flex items-center gap-2">
                  <Settings className="w-3 h-3" />
                  <span>Customizable</span>
                </div>
              </div>
            </div>

            {/* Copyright and Credits */}
            <div className="space-y-3 pt-4 border-t border-border text-sm">
              <div className="text-center">
                <p className="font-medium">© 2026 MapleOS</p>
                <p className="text-muted-foreground">All rights reserved.</p>
              </div>
              
              <div className="text-center space-y-1">
                <p className="text-xs text-muted-foreground">
                  Powered by Monaco Editor, React, and TypeScript
                </p>
                <div className="flex items-center justify-center gap-1 text-xs text-muted-foreground">
                  <span>Made with</span>
                  <Heart className="w-3 h-3 text-red-500 fill-current" />
                  <span>for developers worldwide</span>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end gap-2 pt-4 border-t border-border">
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => window.open('https://canxp.ai/desktop', '_blank')}
              >
                <ExternalLink className="w-4 h-4 mr-2" />
                Visit Website
              </Button>
              <Button onClick={() => setShowAboutDialog(false)} size="sm">
                OK
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Open Project Dialog */}
      <Dialog open={showOpenProjectDialog} onOpenChange={setShowOpenProjectDialog}>
        <DialogContent className="sm:max-w-[600px] h-[500px] z-[9999]">
          <DialogHeader>
            <DialogTitle>New Project</DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-hidden">
            <ScrollArea className="h-full">
              {availableProjects.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-64 text-center">
                  <FolderOpen className="w-16 h-16 text-muted-foreground mb-4" />
                  <h3 className="text-lg font-semibold mb-2">No Projects Found</h3>
                  <p className="text-muted-foreground mb-4">
                    Create a new project or import an existing one to get started.
                  </p>
                  <div className="flex gap-2">
                    <Button onClick={() => {
                      setShowOpenProjectDialog(false);
                      setShowProjectManager(true);
                    }}>
                      <FolderPlus className="w-4 h-4 mr-2" />
                      New Project
                    </Button>
                    <Button variant="outline" onClick={() => {
                      setShowOpenProjectDialog(false);
                      setShowImportDialog(true);
                    }}>
                      <Upload className="w-4 h-4 mr-2" />
                      Import Project
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="space-y-3 p-1">
                  {availableProjects.map((project) => (
                    <Card 
                      key={project.id} 
                      className={`cursor-pointer transition-all hover:shadow-md hover:border-primary/50 ${
                        currentProject?.id === project.id ? 'border-primary bg-primary/5' : ''
                      }`}
                      onClick={() => handleOpenProject(project)}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <Folder className="w-5 h-5 text-primary" />
                              <h3 className="font-semibold">{project.name}</h3>
                              {currentProject?.id === project.id && (
                                <span className="text-xs bg-primary text-primary-foreground px-2 py-1 rounded">
                                  Current
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-muted-foreground mb-2">
                              {project.description || 'No description available'}
                            </p>
                            <div className="flex items-center gap-4 text-xs text-muted-foreground">
                              <span>Type: {project.type}</span>
                              <span>Created: {new Date(project.created).toLocaleDateString()}</span>
                              <span>Modified: {new Date(project.lastModified).toLocaleDateString()}</span>
                            </div>
                            <div className="text-xs text-muted-foreground mt-1">
                              Path: {project.path}
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </ScrollArea>
          </div>
          <div className="flex justify-end gap-2 pt-4 border-t border-border">
            <Button variant="outline" onClick={() => setShowOpenProjectDialog(false)}>
              Cancel
            </Button>
            <Button onClick={() => {
              setShowOpenProjectDialog(false);
              setShowProjectManager(true);
            }}>
              <FolderPlus className="w-4 h-4 mr-2" />
              New Project
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* React Runner Window */}
      {showRunner && (
        <div className="fixed inset-0 z-[10000] bg-black/50 flex items-center justify-center p-4">
          <div 
            className="bg-background border rounded-lg shadow-2xl w-full max-w-6xl h-[85vh] flex flex-col react-runner-window"
            style={{ maxWidth: '90vw' }}
          >
            {/* OS-Style Window Titlebar */}
            <div className="flex items-center justify-between px-3 py-1.5 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-t-lg border-b">
              <div className="flex items-center gap-2">
                <Play className="w-4 h-4" />
                <span className="font-medium text-sm">React App Runner</span>
              </div>
              <div className="flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 w-6 p-0 hover:bg-white/20 text-white"
                  onClick={() => {/* Minimize - future feature */}}
                >
                  <Minus className="w-3 h-3" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 w-6 p-0 hover:bg-white/20 text-white"
                  onClick={() => {
                    const element = document.querySelector('.react-runner-window') as HTMLElement;
                    const container = element?.parentElement;
                    if (element && container) {
                      if (element.dataset.maximized === 'true') {
                        // Restore
                        element.dataset.maximized = 'false';
                        container.className = 'fixed inset-0 z-[10000] bg-black/50 flex items-center justify-center p-4';
                        element.style.cssText = 'max-width: 90vw';
                      } else {
                        // Maximize
                        element.dataset.maximized = 'true';
                        container.className = 'fixed inset-0 z-[10000] bg-black/50';
                        element.style.cssText = 'width: 100vw; height: 100vh; max-width: none; border-radius: 0;';
                      }
                    }
                  }}
                >
                  <Square className="w-3 h-3" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 w-6 p-0 hover:bg-white/20 text-white"
                  onClick={() => setShowRunner(false)}
                >
                  <X className="w-3 h-3" />
                </Button>
              </div>
            </div>
            
            {/* Window Content */}
            <div className="flex-1 overflow-hidden">
              <ReactRunner 
                code={activeTab ? activeTab.content : ''}
                language={activeTab ? getLanguageFromPath(activeTab.path) : 'javascript'}
              />
            </div>
          </div>
        </div>
      )}

      {/* C++ Runner Window */}
      {showCppRunner && (
        <div className="fixed inset-0 z-[10000] bg-black/50 flex items-center justify-center p-4">
          <div 
            className="bg-background border rounded-lg shadow-2xl w-full max-w-6xl h-[85vh] flex flex-col cpp-runner-window"
            style={{ maxWidth: '90vw' }}
          >
            {/* OS-Style Window Titlebar */}
            <div className="flex items-center justify-between px-3 py-1.5 bg-gradient-to-r from-purple-500 to-purple-600 text-white rounded-t-lg border-b">
              <div className="flex items-center gap-2">
                <Gamepad2 className="w-4 h-4" />
                <span className="font-medium text-sm">C++ WASM-4 Compiler</span>
              </div>
              <div className="flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 w-6 p-0 hover:bg-white/20 text-white"
                  onClick={() => {/* Minimize - future feature */}}
                >
                  <Minus className="w-3 h-3" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 w-6 p-0 hover:bg-white/20 text-white"
                  onClick={() => {
                    const element = document.querySelector('.cpp-runner-window') as HTMLElement;
                    const container = element?.parentElement;
                    if (element && container) {
                      if (element.dataset.maximized === 'true') {
                        // Restore
                        element.dataset.maximized = 'false';
                        container.className = 'fixed inset-0 z-[10000] bg-black/50 flex items-center justify-center p-4';
                        element.style.cssText = 'max-width: 90vw';
                      } else {
                        // Maximize
                        element.dataset.maximized = 'true';
                        container.className = 'fixed inset-0 z-[10000] bg-black/50';
                        element.style.cssText = 'width: 100vw; height: 100vh; max-width: none; border-radius: 0;';
                      }
                    }
                  }}
                >
                  <Square className="w-3 h-3" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 w-6 p-0 hover:bg-white/20 text-white"
                  onClick={() => setShowCppRunner(false)}
                >
                  <X className="w-3 h-3" />
                </Button>
              </div>
            </div>
            
            {/* Window Content */}
            <div className="flex-1 overflow-hidden">
              <CppRunner 
                code={(() => {
                  const currentTab = tabs.find(tab => tab.id === activeTabId);
                  return currentTab ? currentTab.content : '';
                })()}
                language={(() => {
                  const currentTab = tabs.find(tab => tab.id === activeTabId);
                  return currentTab ? getLanguageFromPath(currentTab.path) : 'cpp';
                })()}
              />
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default CodeEditor;